import { z } from 'zod';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { ApiError } from './api';
import { PlannedWorkoutSchema } from './workouts';

/**
 * Cliente HTTP + hooks para el Coach IA (`POST /api/coach/plan`).
 *
 * Como en `strava.ts`, NO reutilizamos el helper `request` de `./api`: el
 * endpoint del coach tiene un mapeo de errores muy específico (503 = key no
 * configurada, 502 = error de Anthropic, 422 = plan inválido), y necesitamos
 * exponer las causas concretas para que la UI pueda dar pistas accionables al
 * usuario.
 *
 * Fuente única de `PlannedWorkoutSchema`/`Discipline`/`WorkoutStatus`:
 * `lib/workouts.ts`. Aquí los **re-exportamos** para no romper los imports
 * históricos `from '@/lib/coach'` que aún existen en el código y tests.
 */

// Re-exports (la fuente única vive en `lib/workouts.ts`)
export {
  DISCIPLINES,
  PlannedWorkoutSchema,
  WORKOUT_STATUSES,
  type Discipline,
  type PlannedWorkout,
  type WorkoutStatus,
} from './workouts';

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Errores específicos del Coach
// ---------------------------------------------------------------------------

/**
 * Backend no tiene API key de Anthropic configurada para el profile en curso
 * (desde M1.10 vive por profile, no en `.env`). El `hint` original del backend
 * apunta al usuario hacia `/ajustes/anthropic` para subsanarlo.
 */
export class CoachNotConfiguredError extends ApiError {
  public readonly hint: string;
  constructor(hint = '') {
    super('Coach IA no configurado en el backend', 503);
    this.name = 'CoachNotConfiguredError';
    this.hint = hint;
  }
}

/**
 * El profile alcanzó su presupuesto mensual de tokens Anthropic. Se emite
 * desde `POST /api/coach/plan` con `503 { error: 'anthropic_budget_exceeded' }`.
 * La UI debe explicar que se puede subir el presupuesto en `/ajustes/anthropic`
 * o esperar al mes siguiente.
 */
export class CoachBudgetExceededError extends ApiError {
  public readonly hint: string;
  constructor(hint = '') {
    super('Presupuesto Anthropic agotado', 503);
    this.name = 'CoachBudgetExceededError';
    this.hint = hint;
  }
}

/** Error 5xx del API de Anthropic. */
export class CoachUpstreamError extends ApiError {
  constructor() {
    super('anthropic_error', 502);
    this.name = 'CoachUpstreamError';
  }
}

/**
 * El modelo generó un plan que no cumple las reglas de validación del backend
 * (estructura, total de horas, etc.). El `detail` es el mensaje humano que
 * acompaña a la respuesta 422.
 */
export class CoachInvalidPlanError extends ApiError {
  public readonly detail: string;
  constructor(detail = '') {
    super('Plan generado inválido', 422);
    this.name = 'CoachInvalidPlanError';
    this.detail = detail;
  }
}

/**
 * El body enviado no pasó validación del backend (issues de Zod). `issues` se
 * mantiene como string[] de mensajes para mostrar inline cuando es trivial.
 */
export class CoachBadRequestError extends ApiError {
  public readonly issues: string[];
  constructor(issues: string[] = [], detail = '') {
    super(detail || 'Petición inválida', 400);
    this.name = 'CoachBadRequestError';
    this.issues = issues;
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export const GOAL_VALUES = [
  'recovery',
  'base',
  'build',
  'peak',
  'taper',
  'test',
  'race_week',
] as const;
export type Goal = (typeof GOAL_VALUES)[number];

export const HoursPerDaySchema = z.object({
  mon: z.number().min(0).max(24),
  tue: z.number().min(0).max(24),
  wed: z.number().min(0).max(24),
  thu: z.number().min(0).max(24),
  fri: z.number().min(0).max(24),
  sat: z.number().min(0).max(24),
  sun: z.number().min(0).max(24),
});
export type HoursPerDay = z.infer<typeof HoursPerDaySchema>;

/**
 * M2.25 — `'auto'` es valor especial: indica al backend que derive el goal
 * y opcionalmente las horas a partir del plan de temporada activo (fase
 * actual). Si no hay plan activo el backend devuelve 400 `auto_no_season`.
 */
export const GOAL_INPUT_VALUES = [...GOAL_VALUES, 'auto'] as const;
export type GoalInput = (typeof GOAL_INPUT_VALUES)[number];

export const CoachPlanRequestSchema = z.object({
  weekStartISO: z.string().min(1),
  goal: z.enum(GOAL_INPUT_VALUES),
  // M2.25 — opcional cuando goal='auto' (el backend lo genera desde el
  // targetWeeklyHours de la fase activa).
  hoursPerDay: HoursPerDaySchema.optional(),
  constraints: z.string().max(1000).optional(),
  preview: z.boolean().optional(),
});
export type CoachPlanRequest = z.infer<typeof CoachPlanRequestSchema>;

export const PlanSummarySchema = z.object({
  totalHours: z.number().nonnegative(),
  byDiscipline: z
    .object({
      swim: z.number().nonnegative().optional(),
      bike: z.number().nonnegative().optional(),
      run: z.number().nonnegative().optional(),
      strength: z.number().nonnegative().optional(),
      brick: z.number().nonnegative().optional(),
    })
    .partial(),
});
export type PlanSummary = z.infer<typeof PlanSummarySchema>;

export const CoachPlanSchema = z.object({
  weekStartISO: z.string(),
  weekEndISO: z.string(),
  workouts: z.array(PlannedWorkoutSchema),
  summary: PlanSummarySchema,
  rationale: z.string(),
});
export type CoachPlan = z.infer<typeof CoachPlanSchema>;

export const CoachPlanResponseSchema = z.object({
  plan: CoachPlanSchema,
  tokensUsed: z.number().int().nonnegative(),
  latencyMs: z.number().nonnegative(),
  modelUsed: z.string(),
  persisted: z.boolean(),
});
export type CoachPlanResponse = z.infer<typeof CoachPlanResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function pickIssues(body: unknown): string[] {
  if (!body || typeof body !== 'object') return [];
  const issues = (body as Record<string, unknown>).issues;
  if (!Array.isArray(issues)) return [];
  const out: string[] = [];
  for (const issue of issues) {
    if (typeof issue === 'string') {
      out.push(issue);
    } else if (issue && typeof issue === 'object') {
      const msg = (issue as Record<string, unknown>).message;
      if (typeof msg === 'string') out.push(msg);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Endpoint
// ---------------------------------------------------------------------------

/**
 * POST /api/coach/plan
 *
 * Genera (o previsualiza) un plan semanal. Latencia esperada: 10-30s.
 */
export async function generatePlan(body: CoachPlanRequest): Promise<CoachPlanResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/coach/plan`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = CoachPlanResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError('Respuesta /api/coach/plan no cumple esquema', 200, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  const errStr = pickString(json, 'error') ?? '';
  const detail = pickString(json, 'detail') ?? pickString(json, 'message') ?? '';
  const hint = pickString(json, 'hint') ?? '';

  if (response.status === 503) {
    // Desde M1.10 hay dos motivos distintos de 503 en este endpoint: la key no
    // está configurada para el profile, o se ha agotado el presupuesto mensual.
    // El backend los distingue con `error: 'anthropic_budget_exceeded'` o
    // 'anthropic_no_configurado' / 'coach_not_configured' (legacy). Mapeamos
    // ambos a tipos diferentes para que la UI pueda guiar al usuario al
    // ajuste correcto.
    if (errStr === 'anthropic_budget_exceeded') {
      throw new CoachBudgetExceededError(hint);
    }
    throw new CoachNotConfiguredError(hint);
  }
  if (response.status === 502 || errStr === 'anthropic_error') {
    throw new CoachUpstreamError();
  }
  if (response.status === 422) {
    throw new CoachInvalidPlanError(detail);
  }
  if (response.status === 400) {
    throw new CoachBadRequestError(pickIssues(json), detail);
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

// ---------------------------------------------------------------------------
// Hook (TanStack Query)
// ---------------------------------------------------------------------------

/**
 * `useGeneratePlan` — mutation.
 *
 * - Al éxito, si `persisted: true` (es decir, no era preview), invalida
 *   `['workouts']` para que la vista de calendario se refresque cuando exista.
 * - Si es solo preview, NO invalida nada — el usuario aún debe aceptarlo.
 */
export function useGeneratePlan(): UseMutationResult<
  CoachPlanResponse,
  Error,
  CoachPlanRequest
> {
  const qc = useQueryClient();
  return useMutation<CoachPlanResponse, Error, CoachPlanRequest>({
    mutationFn: (body) => generatePlan(body),
    onSuccess: async (data) => {
      if (data.persisted) {
        await qc.invalidateQueries({ queryKey: ['workouts'] });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// M2.32.C — Reajuste dinámico de la semana
// ---------------------------------------------------------------------------

export const RESCHEDULE_REASONS = ['skipped', 'partial', 'extra_time', 'auto'] as const;
export type RescheduleReason = (typeof RESCHEDULE_REASONS)[number];

export const RescheduleRequestSchema = z.object({
  weekStartISO: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD requerido'),
  reason: z.enum(RESCHEDULE_REASONS).optional(),
});
export type RescheduleRequest = z.infer<typeof RescheduleRequestSchema>;

export const RescheduleResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  planneds: z.array(PlannedWorkoutSchema),
  rationale: z.string(),
  tokensUsed: z.number().int().nonnegative(),
  latencyMs: z.number().nonnegative(),
  modelUsed: z.string(),
});
export type RescheduleResponse = z.infer<typeof RescheduleResponseSchema>;

/**
 * Error específico cuando no hay nada que reajustar (todo completed) o no
 * hay plan activo en la semana indicada. El handler responde 409.
 */
export class RescheduleNoChangesError extends ApiError {
  public readonly detail: string;
  constructor(detail = '') {
    super(detail || 'Nada que reajustar', 409);
    this.name = 'RescheduleNoChangesError';
    this.detail = detail;
  }
}

/**
 * POST /api/coach/reschedule
 *
 * Reajusta los planneds PENDING/MISSED de la semana indicada. Latencia
 * típica: 10-30s (igual que generatePlan).
 */
export async function rescheduleWeek(
  body: RescheduleRequest,
): Promise<RescheduleResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/coach/reschedule`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = RescheduleResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/coach/reschedule no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  const errStr = pickString(json, 'error') ?? '';
  const detail = pickString(json, 'detail') ?? pickString(json, 'message') ?? '';
  const hint = pickString(json, 'hint') ?? '';

  if (response.status === 503) {
    if (errStr === 'anthropic_budget_exceeded') {
      throw new CoachBudgetExceededError(hint);
    }
    throw new CoachNotConfiguredError(hint);
  }
  if (response.status === 502 || errStr === 'anthropic_error') {
    throw new CoachUpstreamError();
  }
  if (response.status === 409) {
    throw new RescheduleNoChangesError(detail || errStr);
  }
  if (response.status === 422) {
    throw new CoachInvalidPlanError(detail);
  }
  if (response.status === 400) {
    throw new CoachBadRequestError(pickIssues(json), detail);
  }

  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

/**
 * `useRescheduleWeek` — mutation. Invalida `['workouts']` al éxito para que
 * el calendario se refresque.
 */
export function useRescheduleWeek(): UseMutationResult<
  RescheduleResponse,
  Error,
  RescheduleRequest
> {
  const qc = useQueryClient();
  return useMutation<RescheduleResponse, Error, RescheduleRequest>({
    mutationFn: (body) => rescheduleWeek(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['workouts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// M2.47 — Importar plan del entrenador (parse texto libre → estructurado)
// ---------------------------------------------------------------------------

const PLANNED_DISCIPLINES_PARSE = [
  'swim',
  'bike',
  'run',
  'strength',
  'brick',
] as const;
const INTENSITY_ZONES_PARSE = [
  'z1',
  'z2',
  'z3',
  'z4',
  'z5',
  'z6',
  'z7',
  'recovery',
  'threshold',
  'vo2max',
  'other',
] as const;

export const ParsePlanEntrySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  discipline: z.enum(PLANNED_DISCIPLINES_PARSE),
  durationPlannedMin: z.number().int().min(10).max(600),
  intensityZone: z.enum(INTENSITY_ZONES_PARSE).nullable(),
  description: z.string().max(2000),
  confidence: z.enum(['high', 'medium', 'low']),
});
export type ParsePlanEntry = z.infer<typeof ParsePlanEntrySchema>;

export const ParsePlanImportResponseSchema = z.object({
  entries: z.array(ParsePlanEntrySchema),
  notes: z.string().nullable(),
  timezone: z.string(),
  tokensUsed: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
  modelUsed: z.string(),
});
export type ParsePlanImportResponse = z.infer<
  typeof ParsePlanImportResponseSchema
>;

export interface ParsePlanImportRequest {
  text: string;
  referenceDateISO?: string;
}

export async function parsePlanImport(
  body: ParsePlanImportRequest,
): Promise<ParsePlanImportResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/coach/parse-plan-import`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = ParsePlanImportResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/coach/parse-plan-import no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }
  const json = await readJsonSafe(response);
  const errStr = pickString(json, 'error') ?? '';
  const hint = pickString(json, 'hint') ?? '';
  if (response.status === 503) {
    if (errStr === 'anthropic_budget_exceeded') {
      throw new CoachBudgetExceededError(hint);
    }
    throw new CoachNotConfiguredError(hint);
  }
  if (response.status === 502 || errStr === 'anthropic_error') {
    throw new CoachUpstreamError();
  }
  if (response.status === 422) {
    throw new CoachInvalidPlanError(
      pickString(json, 'detail') ?? 'No se pudo parsear el plan.',
    );
  }
  if (response.status === 400) {
    throw new CoachBadRequestError(pickIssues(json));
  }
  throw new ApiError(`Respuesta no OK: ${response.status}`, response.status);
}

export function useParsePlanImport(): UseMutationResult<
  ParsePlanImportResponse,
  Error,
  ParsePlanImportRequest
> {
  return useMutation<ParsePlanImportResponse, Error, ParsePlanImportRequest>({
    mutationFn: (body) => parsePlanImport(body),
  });
}
